import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import moment from "moment/moment";
import {
  IconChevronLeft,
  IconChevronRight,
  IconCurrencyEuro,
  IconDotsVertical,
  IconDownload,
  IconFileText,
  IconPlus,
  IconReceipt,
  IconTrendingDown,
  IconTrendingUp,
} from "@tabler/icons-react";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions";
// @ts-expect-error pure js
import { setBalanceAmountSignFromType } from "../../utils";
// @ts-expect-error pure js
import useBalances, { useBalanceTypes } from "../../hooks/use-balances";
// @ts-expect-error pure js
import useAuthenticatedAxios from "../../hooks/use-authenticated-axios";
// @ts-expect-error pure js
import DocumentDownloader from "../UI/DocumentDownloader";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils/index.jsx";
import { useToast } from "../UI/Toast/useToast";
// @ts-expect-error pure js
import useTenants from "../../hooks/use-tenants";
// @ts-expect-error pure js
import useProperties from "../../hooks/use-properties";
// @ts-expect-error pure js
import usePropertyFolders from "../../hooks/use-property-folders";
import { ChargesType } from "../../utils/constants/tenants";
// @ts-expect-error pure js
import useProfileBailleurs from "../../hooks/use-profile-bailleurs";
// @ts-expect-error pure js
import BalanceForm from "../Balance/BalanceForm";
import {
  BalanceDomain,
  BalanceTypeCategory,
  DOMAIN_TO_COMPTA_ROUTE,
} from "../../utils/constants/balances";
import {
  getAffectationLabel,
  getBalanceParentId,
  getBalanceText,
  isChargeARegulariser,
} from "../../utils/balances";
import useGroupedBalances from "../../hooks/use-grouped-balances";
// @ts-expect-error pure js
import { sanitizeValues } from "../../models/utils";
// @ts-expect-error pure js
import BalanceMasseForm from "../Balance/BalanceMasseForm";
import Button from "../UI/Button";
import DataCard from "../UI/DataCard";
import DropdownMenu from "../UI/DropdownMenu.tsx";
import Title from "../UI/Title.tsx";
import Text from "../UI/Text";
// @ts-expect-error pure js
import { ReactSelectStandalone } from "../UI/SelectStandalone";
import BalanceTable from "../Balance/BalanceTable";
import BalanceFilterBar from "../Balance/BalanceFilterBar";
import useBalanceFilters from "../../hooks/use-balance-filters";
import type { Balance } from "../../models/balance";
import type { Property } from "../../models/property";
import BalanceRowActions from "../Balance/BalanceRowActions";
import styles from "./ComptaShow.module.css";
import IconButton from "../UI/IconButton.tsx";

interface BalanceTypeInfo {
  static_id: string;
  text: string;
}

interface ComptaShowProps {
  parentId: number;
  domaineBalance: string;
  propertyType?: string;
  soldeTitle?: string;
  /** Entrées supplémentaires du menu « Plus d'options ». */
  extraMenuItems?: ReactNode;
}

const ALL_YEARS_FILTER = "all";

export default function ComptaShow({
  parentId,
  domaineBalance,
  propertyType,
  soldeTitle = "Solde comptable du bien",
  extraMenuItems,
}: ComptaShowProps) {
  const { balanceTypes, balanceTypesByCategory, isLoading } = useBalanceTypes();
  const {
    balances,
    createBalance,
    batchCreateBalances,
    duplicateBalance,
    updateBalance,
    deleteBalance,
    deleteGroupedBalance,
    batchUpdateBalances,
    refreshBalances,
  } = useBalances(domaineBalance, parentId);
  const { tenantsById } = useTenants({ withById: true });
  const { propertiesById } = useProperties({ withById: true });
  const { propertyFoldersById } = usePropertyFolders({ withById: true });
  const { profileBailleursById } = useProfileBailleurs({ withById: true });

  const axios = useAuthenticatedAxios();
  const toast = useToast();
  const [isBalanceModalOpen, setIsBalanceModalOpen] = useState(false);
  const [isBatchUpdateOpen, setIsBatchUpdateOpen] = useState(false);
  const [balanceToEdit, setBalanceToEdit] = useState<Balance | undefined>();
  const [yearFilter, setYearFilter] = useState<string | null>(null);

  const uniqueYears = useMemo(() => {
    const years = new Set<string>();
    balances.forEach((balance: Balance) => {
      years.add(new Date(balance.date_transac).getFullYear().toString());
    });
    return [...years].sort((a, b) => Number(b) - Number(a));
  }, [balances]);

  const defaultYear = uniqueYears[0] ?? ALL_YEARS_FILTER;
  const activeYearFilter = yearFilter ?? defaultYear;

  const currentYearIndex = uniqueYears.indexOf(activeYearFilter);
  const canGoNext = currentYearIndex > 0;
  const canGoPrev =
    activeYearFilter !== ALL_YEARS_FILTER &&
    currentYearIndex < uniqueYears.length - 1;

  function goToPrevYear() {
    if (canGoPrev) {
      setYearFilter(uniqueYears[currentYearIndex + 1]);
    }
  }

  function goToNextYear() {
    if (canGoNext) {
      setYearFilter(uniqueYears[currentYearIndex - 1]);
    }
  }

  const filteredBalances = useMemo(() => {
    if (activeYearFilter === ALL_YEARS_FILTER) {
      return balances;
    }
    return balances.filter((balance: Balance) =>
      moment(balance.date_transac).isSame(`${activeYearFilter}-01-01`, "year"),
    );
  }, [balances, activeYearFilter]);

  const getBalanceLabel = (balance: Balance) =>
    getBalanceText(balance, {
      balanceTypes,
      domaineBalance,
      tenantsById,
      propertiesById,
    });
  const balanceFilters = useBalanceFilters({
    balances: filteredBalances,
    getBalanceLabel,
    getTypeLabel: (type: string) => balanceTypes?.[type]?.text ?? "",
  });

  const groupedFilteredBalances = useGroupedBalances<Balance>(
    balanceFilters.filteredBalances,
    domaineBalance,
    parentId,
  );

  const excludedBalances = useMemo(() => {
    if (activeYearFilter === ALL_YEARS_FILTER) {
      return [];
    }
    return balances.filter(
      (balance: Balance) =>
        !moment(balance.date_transac).isSame(
          `${activeYearFilter}-01-01`,
          "year",
        ),
    );
  }, [balances, activeYearFilter]);

  const totals = useMemo(() => {
    let totalDebit = 0;
    let totalCredit = 0;
    let totalDgDebit = 0;
    let totalDgCredit = 0;
    let totalDeductible = 0;

    balanceFilters.filteredBalances.forEach((balance: Balance) => {
      const isDg =
        balanceTypesByCategory?.[BalanceTypeCategory.DG_CREDIT]?.some(
          (bt: BalanceTypeInfo) => bt.static_id === balance.type,
        ) ||
        balanceTypesByCategory?.[BalanceTypeCategory.DG_DEBIT]?.some(
          (bt: BalanceTypeInfo) => bt.static_id === balance.type,
        );

      if (balance.montant <= 0) {
        totalDebit += balance.montant;
        // Part déductible = montant hors part refacturée aux locataires
        // (charge récupérable, TEOM d'une taxe foncière).
        if (balanceTypes?.[balance.type]?.is_deductible) {
          totalDeductible +=
            balance.montant - (balance.montant_recuperable ?? 0);
        }
        if (isDg) totalDgDebit += balance.montant;
      } else {
        totalCredit += balance.montant;
        if (isDg) totalDgCredit += balance.montant;
      }
    });

    return {
      debit: totalDebit,
      credit: totalCredit,
      dgDebit: totalDgDebit,
      dgCredit: totalDgCredit,
      deductible: totalDeductible,
    };
  }, [balanceFilters.filteredBalances, balanceTypesByCategory, balanceTypes]);

  const balanceTotal = totals.credit + totals.debit;

  const chargesARegulariser = useMemo(
    () =>
      balanceFilters.filteredBalances
        .filter((balance: Balance) =>
          isChargeARegulariser(balance, balanceTypes),
        )
        .reduce(
          (sum: number, balance: Balance) => sum + Math.abs(balance.montant),
          0,
        ),
    [balanceFilters.filteredBalances, balanceTypes],
  );
  // Une régularisation n'a de sens qu'avec une provision sur charges, pas avec un forfait.
  const isProvision = (property?: Property) =>
    property?.type_charge === ChargesType.PROVISION;
  const showChargesARegulariser =
    domaineBalance === BalanceDomain.PROPERTY
      ? isProvision(propertiesById[parentId])
      : domaineBalance === BalanceDomain.FOLDER &&
        (propertyFoldersById[parentId]?.properties ?? []).some(isProvision);

  if (isLoading) return null;

  async function deleteHandler(id: number | string, isGrouped = false) {
    if (isGrouped) {
      await deleteGroupedBalance(id);
    } else {
      await deleteBalance(id);
    }
  }

  async function handleDelete(balance: Balance) {
    const isGrouped = balance.balances && balance.balances.length > 0;

    if (window.confirm("Voulez-vous vraiment supprimer cette entrée ?")) {
      await deleteHandler(
        isGrouped ? balance.correlation_id! : balance.id,
        isGrouped,
      );
    }
  }

  // Nomme l'entité visée et pointe vers sa compta, quand l'écriture n'atterrit pas dans
  // la liste affichée.
  function notifyElsewhere(
    title: string,
    targetDomain: string,
    targetId: number,
  ) {
    const comptaRoute =
      DOMAIN_TO_COMPTA_ROUTE[
        targetDomain as keyof typeof DOMAIN_TO_COMPTA_ROUTE
      ]?.(targetId) ?? "/bailleur";
    const targetLabel = getAffectationLabel(targetDomain, targetId, {
      tenantsById,
      propertiesById,
      propertyFoldersById,
      profileBailleursById,
    });
    toast.success({
      title,
      description: (
        <>
          Sur{" "}
          <Text as="span" weight="bold">
            {targetLabel}
          </Text>{" "}
          · <Link to={comptaRoute}>Voir sa compta</Link>
        </>
      ),
    });
  }

  async function submitBalanceHandler(
    edit: boolean,
    values: Record<string, unknown>,
  ) {
    const sanitizedValues = { ...sanitizeValues(values) };
    sanitizedValues.montant = setBalanceAmountSignFromType(
      sanitizedValues,
      balanceTypesByCategory,
      sanitizedValues.is_remboursement,
    );
    delete sanitizedValues.is_remboursement;

    const montantSign = Math.sign(sanitizedValues.montant) || 1;
    sanitizedValues.balances = sanitizedValues.balances?.map(
      (balance: Balance) => {
        return {
          ...balance,
          montant: Math.abs(balance.montant) * montantSign,
        };
      },
    );
    const isSplit =
      sanitizedValues.balances && sanitizedValues.balances.length > 0;

    if (edit && !isSplit && balanceToEdit) {
      const targetDomain = sanitizedValues.domaine as string;
      const targetId = Number(getBalanceParentId(sanitizedValues));
      // Le déplacement se mesure par rapport à l'écriture d'origine : c'est elle qui
      // quitte la liste courante.
      const isElsewhere =
        targetDomain !== balanceToEdit.domaine ||
        targetId !== Number(getBalanceParentId(balanceToEdit));

      await updateBalance(balanceToEdit.id, sanitizedValues);
      if (isElsewhere) {
        await refreshBalances();
        notifyElsewhere("Écriture déplacée", targetDomain, targetId);
      }
    } else if (isSplit) {
      const bals = sanitizedValues.balances.map((balance: Balance) => {
        const commonFields = {
          type: sanitizedValues.type,
          date_transac: sanitizedValues.date_transac,
          comment: sanitizedValues.comment,
          file: sanitizedValues.file,
          periode_debut: sanitizedValues.periode_debut || null,
          periode_fin: sanitizedValues.periode_fin || null,
          refacturations:
            sanitizedValues.refacturations || balance.refacturations || null,
          indexes: sanitizedValues.indexes ?? {},
        };
        return { ...balance, ...commonFields };
      });
      const isOldFolderBalance =
        edit && balanceToEdit && !balanceToEdit.balances;
      if (!edit || isOldFolderBalance) {
        await batchCreateBalances(bals);
        if (isOldFolderBalance && balanceToEdit) {
          await deleteBalance(balanceToEdit.id);
        }
      } else if (balanceToEdit) {
        await batchUpdateBalances(bals, {
          requestOptions: {
            params: {
              correlation_id: balanceToEdit.correlation_id,
            },
          },
        });
      }
    } else {
      const targetDomain = sanitizedValues.domaine as string;
      const targetId = Number(getBalanceParentId(sanitizedValues));
      const isElsewhere =
        targetDomain !== domaineBalance || targetId !== parentId;

      await createBalance(sanitizedValues, { shouldMutate: !isElsewhere });
      if (isElsewhere) {
        await refreshBalances();
        notifyElsewhere("Écriture ajoutée", targetDomain, targetId);
      }
    }
    setIsBalanceModalOpen(false);
  }

  async function submitMasseBalanceHandler(updatedBalances: Balance[]) {
    await batchUpdateBalances([...updatedBalances, ...excludedBalances]);
    setIsBatchUpdateOpen(false);
  }

  const renderRowActions = (balance: Balance) => (
    <BalanceRowActions
      balance={balance}
      onEdit={() => {
        setBalanceToEdit(balance);
        setIsBalanceModalOpen(true);
      }}
      onDuplicate={() => duplicateBalance(balance.id)}
      onDelete={() => handleDelete(balance)}
    />
  );

  return (
    <>
      <BalanceForm
        balance={balanceToEdit}
        open={isBalanceModalOpen}
        onOpenChange={(open: boolean) => {
          setIsBalanceModalOpen(open);
          if (!open) setBalanceToEdit(undefined);
        }}
        domaineBalance={domaineBalance}
        parentId={parentId}
        onSubmit={(values: Record<string, unknown>) =>
          submitBalanceHandler(!!balanceToEdit, values)
        }
        onDelete={async (id: number, isGrouped: boolean) => {
          await deleteHandler(id, isGrouped);
          setIsBalanceModalOpen(false);
        }}
      />
      <BalanceMasseForm
        balances={filteredBalances}
        open={isBatchUpdateOpen}
        onOpenChange={setIsBatchUpdateOpen}
        onSubmit={submitMasseBalanceHandler}
        domaineBalance={domaineBalance}
        propertyType={propertyType}
      />

      <div className={styles.yearHeader}>
        <Title variant="deca" weight="bold">
          Comptabilité
        </Title>
        <div className={styles.yearSelector}>
          <IconButton
            label="Année précédente"
            onClick={goToPrevYear}
            disabled={!canGoPrev}
            icon={IconChevronLeft}
            variant="plain"
          />
          <ReactSelectStandalone
            onChange={(option: { value: string }) =>
              setYearFilter(option.value)
            }
            value={{
              value: activeYearFilter,
              label:
                activeYearFilter === ALL_YEARS_FILTER
                  ? "Tous"
                  : activeYearFilter,
            }}
            options={[
              ...uniqueYears.map((year: string) => ({
                value: year,
                label: year,
              })),
              { value: ALL_YEARS_FILTER, label: "Tous" },
            ]}
            className={styles.yearSelect}
          />
          <IconButton
            label="Année suivante"
            onClick={goToNextYear}
            disabled={!canGoNext}
            icon={IconChevronRight}
            variant="plain"
          />
        </div>
      </div>

      <DataCard.Grid>
        <DataCard
          title={soldeTitle}
          value={currencyFormatter(balanceTotal, {
            withSymbol: false,
            noDecimals: true,
          })}
          valueSuffix="€"
          icon={IconCurrencyEuro}
          status={balanceTotal >= 0 ? "success" : "danger"}
          statusLabels={{ success: "Positif", danger: "Négatif" }}
        />
        <DataCard
          title="Total des dépenses"
          value={currencyFormatter(totals.debit, {
            withSymbol: false,
            noDecimals: true,
          })}
          valueSuffix="€"
          subtitle={`dont ${currencyFormatter(Math.abs(totals.deductible), {
            noDecimals: true,
          })} déductibles`}
          icon={IconTrendingDown}
        />
        <DataCard
          title="Total des recettes"
          value={currencyFormatter(totals.credit, {
            withSymbol: false,
            noDecimals: true,
          })}
          valueSuffix="€"
          icon={IconTrendingUp}
        />
        {showChargesARegulariser && (
          <DataCard
            title="Charges à régulariser"
            value={currencyFormatter(chargesARegulariser, {
              withSymbol: false,
              noDecimals: true,
            })}
            valueSuffix="€"
            subtitle="Charges récupérables non encore refacturées"
            icon={IconReceipt}
          />
        )}
      </DataCard.Grid>

      <BalanceFilterBar
        filters={balanceFilters}
        leftActions={
          <DocumentDownloader className={styles.leftActions}>
            {(setSignedUrl: (url: string) => void) => (
              <>
                <Button
                  requiresWrite
                  icon={IconPlus}
                  onClick={() => {
                    setBalanceToEdit(undefined);
                    setIsBalanceModalOpen(true);
                  }}
                >
                  Ajouter une opération
                </Button>
                <DropdownMenu
                  icon={IconDotsVertical}
                  label="Plus d'options"
                  showLabel
                >
                  <DropdownMenu.Item
                    icon={IconDownload}
                    label="Exporter"
                    disabled={balanceFilters.filteredBalances.length === 0}
                    onClick={async () => {
                      try {
                        const response = await axios.post("/balances/export", {
                          balance_ids: balanceFilters.filteredBalances.map(
                            (balance: Balance) => balance.id,
                          ),
                        });
                        setSignedUrl(response.data);
                      } catch (error) {
                        toast.error({
                          title: "Impossible d'exporter les écritures",
                          description: handleAPIError(error),
                        });
                      }
                    }}
                  />
                  <DropdownMenu.Item
                    requiresWrite
                    icon={IconFileText}
                    label="Édition groupée"
                    onClick={() => setIsBatchUpdateOpen(true)}
                  />
                  {extraMenuItems}
                </DropdownMenu>
              </>
            )}
          </DocumentDownloader>
        }
      />

      <BalanceTable
        balances={groupedFilteredBalances}
        getBalanceText={getBalanceLabel}
        renderRowActions={renderRowActions}
      />
    </>
  );
}
